import { IcoPlusSmall16, IcoSparkles16, IcoTrash16 } from '@onefootprint/icons';
import type { DataIdentifier, DocumentRequestConfigCustom, DocumentUploadSettings, DocumentVerificationSettings, JsonSchemaObject, TamperSettings } from '@onefootprint/request-types/dashboard';
import { Divider, Form, IconButton, LinkButton } from '@onefootprint/ui';
import { useEffect, useId, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import useDirty from '../../hooks/use-dirty';
import type { ApplyEditMutation } from '../base-node';
import MarkdownInstructionsDialog from '../markdown-instructions-dialog';
import Section from '../section';
import generateMockData from './generate-mock-data';
import PreviewDialog from './preview-dialog';
type CustomDocumentsFormProps = {
  nodeId: string;
  document?: DocumentRequestConfigCustom;
  verificationSettings?: DocumentVerificationSettings;
  isFirmEmployee: boolean;
  mutation: ApplyEditMutation;
  canEdit?: boolean;
};
type FormData = {
  name: string;
  identifier: string;
  description?: string;
  uploadSettings: DocumentUploadSettings;
  supportedDocumentKinds: SupportedDocumentKind[];
  enableVerification: 'enabled' | 'disabled';
  verificationSettings?: Omit<DocumentVerificationSettings, 'schema' | 'tamperSettings'> & {
    schema: string;
  };
  tamperSettings: boolean;
};
type SupportedDocumentKind = {
  value: string;
};
const tamperSettingsToApi = (enabled: boolean): TamperSettings => enabled ? {
  enabled: {}
} : 'disabled';
const isTamperEnabled = (settings?: TamperSettings): boolean => settings === undefined || settings !== 'disabled' && 'enabled' in settings;
const getDefaultValues = (document?: DocumentRequestConfigCustom, verificationSettings?: DocumentVerificationSettings): FormData => {
  if (!document) {
    return {
      name: '',
      identifier: '',
      description: '',
      uploadSettings: 'prefer_upload',
      supportedDocumentKinds: [{
        value: ''
      }],
      enableVerification: 'disabled',
      tamperSettings: true,
      verificationSettings: {
        supportedDocumentKinds: [''],
        mockData: '{}',
        schema: JSON.stringify({
          $schema: 'http://json-schema.org/draft-07/schema#',
          title: 'Schema',
          type: 'object',
          properties: {},
          required: []
        }, null, 2)
      }
    };
  }
  return {
    name: document.data.name,
    identifier: document.data.identifier.replace('document.custom.', ''),
    description: document.data.description,
    uploadSettings: document.data.uploadSettings,
    supportedDocumentKinds: verificationSettings?.supportedDocumentKinds.map(kind => ({
      value: kind
    })) ?? [{
      value: ''
    }],
    enableVerification: verificationSettings ? 'enabled' : 'disabled',
    tamperSettings: isTamperEnabled(verificationSettings?.tamperSettings),
    verificationSettings: verificationSettings ? {
      supportedDocumentKinds: verificationSettings.supportedDocumentKinds,
      mockData: verificationSettings.mockData,
      schema: JSON.stringify(verificationSettings.schema, null, 2)
    } : {
      supportedDocumentKinds: [''],
      mockData: '{}',
      schema: JSON.stringify({
        $schema: 'http://json-schema.org/draft-07/schema#',
        title: 'Schema',
        type: 'object',
        properties: {},
        required: []
      }, null, 2)
    }
  };
};
const CustomDocumentsForm = ({
  nodeId,
  document,
  verificationSettings: initialVerificationSettings,
  isFirmEmployee,
  mutation,
  canEdit
}: CustomDocumentsFormProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'collectDocument.additionalDocuments.custom.form'
  });
  const {
    toggle
  } = useDirty();
  const [showMarkdownInstructions, setShowMarkdownInstructions] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const id = useId();
  const methods = useForm<FormData>({
    defaultValues: getDefaultValues(document, initialVerificationSettings)
  });
  const {
    formState,
    handleSubmit,
    trigger,
    control
  } = methods;
  const [name, description, uploadSettings, verificationSettings, enableVerification] = useWatch({
    control,
    name: ['name', 'description', 'uploadSettings', 'verificationSettings', 'enableVerification']
  });
  const hideMarkdownInstructions = () => setShowMarkdownInstructions(false);
  const hidePreview = () => setShowPreview(false);
  useEffect(() => {
    toggle('custom-document', formState.isDirty);
  }, [formState.isDirty]);
  const {
    fields,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'supportedDocumentKinds'
  });
  const canEditVerificationSettings = isFirmEmployee || !!verificationSettings;
  const onSubmit = async (data: FormData) => {
    const isValid = await trigger(['name', 'identifier'], {
      shouldFocus: true
    });
    if (!isValid) {
      return;
    }
    const identifier: DataIdentifier<string> = `document.custom.${data.identifier}`;
    let parsedSchema: JsonSchemaObject = {};
    try {
      parsedSchema = JSON.parse(data.verificationSettings?.schema ?? '{}');
    } catch (error) {
      console.error('Error parsing schema', error);
    }
    const documentData = {
      name: data.name,
      description: data.description,
      uploadSettings: data.uploadSettings,
      identifier,
      requiresHumanReview: false
    };
    if (data.verificationSettings && data.enableVerification === 'enabled') {
      data.verificationSettings.supportedDocumentKinds = data.supportedDocumentKinds.map(v => v.value);
      // Fill in placeholder mock data from the extraction schema, preserving any
      // values the user has already entered. Saves having to hand-write the
      // sandbox payload; the user can still edit it afterwards.
      data.verificationSettings.mockData = generateMockData(parsedSchema, data.verificationSettings.mockData);
      methods.setValue('verificationSettings.mockData', data.verificationSettings.mockData);
    } else {
      data.verificationSettings = undefined;
    }
    mutation.mutateAsync({
      kind: 'update_node',
      nodeId: nodeId,
      nodeData: {
        document: {
          data: documentData,
          kind: 'custom'
        },
        verificationSettings: data.verificationSettings ? {
          ...data.verificationSettings,
          schema: parsedSchema,
          tamperSettings: tamperSettingsToApi(data.tamperSettings)
        } : undefined,
        kind: 'custom_document'
      }
    });
  };
  return <FormProvider {...methods}>
      <form id="custom-document" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col flex-1 border border-tertiary rounded-default box-border">
          <div className="flex flex-col flex-1 gap-5 p-4">
            <Form.Input name="name" id={`name-${id}`} label={t($ => $.documentLabel)} tooltip={{
            text: t($_0 => $_0.documentLabelTooltip)
          }} placeholder={t($_1 => $_1.documentLabelPlaceholder)} size="compact" rules={{
            required: t($_2 => $_2.fieldRequired)
          }} disabled={!canEdit} />
            <Form.Input name="identifier" id={`identifier-${id}`} label={t($_3 => $_3.identifier)} tooltip={{
            text: t($_4 => $_4.identifierTooltip)
          }} addon={t($_5 => $_5.identifierPlaceholder)} size="compact" rules={{
            required: t($_6 => $_6.fieldRequired),
            validate: (value: string) => {
              if (!value.match(/^[A-Za-z0-9_-]+$/)) {
                return t($_7 => $_7.noSpecialCharacters);
              }
              return true;
            }
          }} disabled={!canEdit} />
            <div className="flex flex-col gap-2">
              <Form.Textarea<FormData> name="description" label={t($_8 => $_8.descriptionLabel)} tooltip={{
              text: t($_9 => $_9.descriptionTooltip)
            }} size="compact" disabled={!canEdit} />
              <div className="text-body-2 text-tertiary">
                <Trans ns="playbook-builder" i18nKey={$_10 => $_10.collectDocument.additionalDocuments.custom.form.markdownInstructions} components={{
                underlined: <u />,
                linkButton: <button type="button" className="cursor-pointer" onClick={() => setShowMarkdownInstructions(true)} />,
                previewLink: <button type="button" className="cursor-pointer" onClick={() => setShowPreview(true)} />
              }} />
              </div>
            </div>
          </div>
          <div className="flex flex-col px-4 pb-5">
            <Divider variant="secondary" />
            <div className="flex flex-col gap-4 pt-4">
              <div className="text-label-2 text-primary">{t($_11 => $_11.collectionMethod.title)}</div>
              <div className="flex flex-col gap-2">
                <Form.RadioGroup<FormData> name="uploadSettings" options={[{
                label: t($_12 => $_12.collectionMethod.preferUpload.label),
                value: 'prefer_upload',
                hint: t($_13 => $_13.collectionMethod.preferUpload.hint)
              }, {
                label: t($_14 => $_14.collectionMethod.preferCapture.label),
                value: 'prefer_capture',
                hint: t($_15 => $_15.collectionMethod.preferCapture.hint)
              }]} disabled={!canEdit} />
              </div>
            </div>
          </div>
        </div>
        {(isFirmEmployee || verificationSettings) && <div className="flex flex-col flex-1 border border-tertiary rounded-default box-border mt-5">
            <div className="flex flex-col flex-1 gap-5 p-4 pt-0">
              <div className="flex flex-col gap-4 pt-4">
                <div className="flex flex-row items-center justify-between w-full">
                  <div className="flex flex-row gap-1 justify-between w-full">
                    <div className="flex flex-row items-center gap-1">
                      <IcoSparkles16 />
                      <div className="text-label-2 text-primary mr-2">Verify with Percy</div>
                    </div>
                    <div className="flex">
                      <Form.Select<FormData> name="enableVerification" size="compact" options={[{
                    label: 'Enabled',
                    value: 'enabled'
                  }, {
                    label: 'Disabled',
                    value: 'disabled'
                  }]} disabled={!canEdit} />
                    </div>
                  </div>
                </div>
                {enableVerification === 'enabled' && <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-4 mb-4">
                      <p className="text-body-2">{'Add all the supported document kinds below'}</p>

                      <div className="flex flex-col gap-1">
                        {fields.map((field, index) => {
                    return <div key={field.id} id={field.id} className="flex flex-row items-center gap-1">
                              <Form.Input name={`supportedDocumentKinds.${index}.value`} label={''} placeholder={'i.e. Bank statement or utility bill'} size="compact" rules={{
                        required: true
                      }} disabled={!canEdit} />
                              {fields.length > 1 && <IconButton type="button" aria-label={'delete document kind'} icon={IcoTrash16} onClick={() => remove(index)} disabled={!canEditVerificationSettings || !canEdit} className="flex-shrink-0" destructive />}
                            </div>;
                  })}
                      </div>
                      <LinkButton type="button" onClick={() => {
                  append({
                    value: ''
                  }, {
                    shouldFocus: false
                  });
                }} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={!canEditVerificationSettings || !canEdit}>
                        {'Add document kind'}
                      </LinkButton>
                    </div>
                    <Section title={t($_16 => $_16.tamperSettings.title)}>
                      <Form.Toggle name="tamperSettings" label={t($_17 => $_17.tamperSettings.label)} hint={t($_18 => $_18.tamperSettings.hint)} size="compact" disabled={!canEditVerificationSettings || !canEdit} />
                    </Section>
                    <Section title={'Extraction'}>
                      <Form.SchemaEditor<FormData> name="verificationSettings.schema" disabled={!canEditVerificationSettings || !canEdit} rules={{
                  required: t($_19 => $_19.schemaEditor.required)
                }} title={t($_20 => $_20.schemaEditor.title)} subtitle={t($_21 => $_21.schemaEditor.subtitle)} />
                    </Section>
                    <Section title={'Mock data (sandbox)'}>
                      <Form.JsonEditor<FormData> name="verificationSettings.mockData" placeholder={''} rules={{
                  required: true
                }} disabled={!canEditVerificationSettings || !canEdit} />
                    </Section>
                  </div>}
              </div>
            </div>
          </div>}
        <MarkdownInstructionsDialog open={showMarkdownInstructions} onClose={hideMarkdownInstructions} />
        <PreviewDialog name={name} description={description} uploadSetting={uploadSettings} open={showPreview} onClose={hidePreview} />
      </form>
    </FormProvider>;
};
export default CustomDocumentsForm;